<template>
  <div>
    <div class="vch-newsletter-signup__email-form">
      <slot name="form">Form Here</slot>
      <p class="vch-newsletter-signup__error text-small" v-if="emailError">
        {{ emailError }}
      </p>
    </div>
    <vch-modal
      displayType="center"
      :open="modalOpen"
      @closed="closeModals"
      :closeAriaLabel="closeAriaLabel"
      :busName="busName"
      :modal-heading-aria-label="modalHeadingAriaLabel"
    >
      <div
        v-if="signupComplete"
        class="vch-newsletter-signup__thankyou-message"
      >
        <h1 id="modal-heading" class="vch-newsletter-signup__modal-title">
          <span class="heading-lg">{{ thankYouTitle }}</span>
          <span class="heading-lg-em">{{ thankYouSubTitle }}</span>
        </h1>
        <p class="text-body-cap">{{ thankYouMessage }}</p>
      </div>
      <div v-else class="vch-newsletter-signup__terms-form">
        <h2 id="modal-heading" class="vch-newsletter-signup__modal-title">
          <span class="heading-md-lg">{{ termsTitle }}</span>
          <span class="heading-md-lg-em">{{ termsSubTitle }}</span>
        </h2>
        <form @submit.prevent="submitForm">
          <label for="confirm-subscription-terms">
            <input
              id="confirm-subscription-terms"
              name="confirm-subscription-terms"
              type="checkbox"
              value="confirm"
              v-model="confirmTermsCheck"
              @change="onTermsCheck"
            />
            <span class="text-body">
              <slot name="terms"></slot>
            </span>
            <div
              v-if="termsFormError !== null"
              class="vch-newsletter-signup__error text-small"
            >
              {{ termsFormError }}
            </div>
            <div
              v-if="recaptchaError !== null"
              class="vch-newsletter-signup__error text-small"
            >
              {{ recaptchaError }}
            </div>
          </label>
          <p>
            <span class="text-body">
              <slot name="recaptcha-terms"></slot>
            </span>
          </p>
          <div class="form-actions">
            <input
              :value="termsSubmitText"
              type="submit"
              class="button -primary"
              :class="{
                'submitDisabled': submitDisabled,
              }"
            />
          </div>
        </form>
      </div>
    </vch-modal>
  </div>
</template>

<script>
import { pushEvent } from 'Utils/datalayer';
import { mixinFormRecaptcha } from 'Mixins/formRecaptcha/formRecaptcha';

export default {
  name: 'vch-newsletter-signup',
  mixins: [mixinFormRecaptcha],
  data() {
    return {
      busName: 'newsletter-signup',
      modalOpen: false,
      formAction: '',
      formMethod: '',
      formData: {},
      emailError: null,
      termsFormError: null,
      confirmTermsCheck: false,
      signupComplete: false,
      parser: new DOMParser(),
      submitDisabled: false,
    };
  },
  props: {
    closeAriaLabel: {
      type: String,
      required: false,
    },
    termsTitle: {
      type: String,
      required: true,
    },
    termsSubTitle: {
      type: String,
      required: true,
    },
    termsError: {
      type: String,
      required: true,
    },
    termsSubmitText: {
      type: String,
      required: true,
    },
    testHost: {
      type: String,
      required: false,
    },
    thankYouTitle: {
      type: String,
      required: true,
    },
    thankYouSubTitle: {
      type: String,
      required: true,
    },
    thankYouMessage: {
      type: String,
      required: true,
    },
    defaultErrorMessage: {
      type: String,
      required: false,
      default: 'An error occured while processing your request.',
    },
    invalidEmailErrorMessage: {
      type: String,
      required: false,
      default: 'The email address entered is invalid.',
    },
    taggingOrigin: {
      type: String,
      required: false,
      default: '',
    },
    modalHeadingAriaLabel:{
      type: String,
      required: false,
      default: 'Modal heading',
    },
  },
  mounted: function () {
    // Form is always wrapped in two divs
    const formNode = this.$slots.form[0].children[0].children[0]?.elm;
    if (!formNode) {
      return;
    }
    // Store the original form action and method for our async call
    this.formAction = formNode?.getAttribute('action');
    this.formMethod = formNode?.getAttribute('method');

    // Grab and store all the extra sitecore form fields for submission
    this.formData = new FormData();
    formNode.querySelectorAll('input').forEach((field) => {
      const fieldName = field.getAttribute('name');
      const fieldValue = field.value;
      this.formData.append(fieldName, fieldValue);
    });

    // Replace the sitecore form element with a clean form element
    // This avoids any event handlers bound to the form by default from sitecore.
    // Existing form tag memory + events should be cleared by garbage collection.
    var formReplacement = document.createElement('form');
    formReplacement.setAttribute('action', '/');
    formReplacement.setAttribute('method', 'post');
    formReplacement.append(...formNode.children);
    formReplacement.addEventListener('submit', (evt) => {
      evt.preventDefault();
      try {
        const emailInput = formReplacement.querySelector('input[type="email"]');
        const emailFieldname = emailInput?.getAttribute('name');
        const inputValue = emailInput?.value;
        let isValid, emailValidated;

        if (
          emailInput.hasAttribute('data-val-required') &&
          !inputValue.length
        ) {
          isValid = false;
          this.emailError = emailInput.getAttribute('data-val-required');
        }
        // If the email field has a validation pattern attribute, use it to validate the input
        else if (emailInput.hasAttribute('data-val-regex-pattern')) {
          emailValidated = true;
          const matcher = new RegExp(
            emailInput.getAttribute('data-val-regex-pattern')
          );
          isValid = matcher.test(inputValue);
          this.emailError = isValid ? null : this.invalidEmailErrorMessage;
        } else {
          isValid = true;
          emailValidated = false;
          this.emailError = '';
        }

        // Attempt to use browser validation if needed
        if (!emailValidated) {
          let browserValid = emailInput?.validity?.valid;
          if (browserValid === null || browserValid === undefined)
            browserValid = true;
          if (!browserValid) {
            this.emailError = this.invalidEmailErrorMessage;
            this.isValid = false;
          }
        }

        if (isValid) {
          this.formData.set(emailFieldname, emailInput?.value);
          this.modalOpen = true;
          // pushEvent({
          //   event: 'uaevent',
          //   event_name: 'newsletter_registration',
          //   ecommerce: 'undefined',
          //   eventCategory: 'registration',
          //   eventAction: 'newsletter',
          //   eventLabel: `${this.taggingOrigin}::step1`,
          //   referral: `${this.taggingOrigin}`,
          //   event_detail: 'step1',
          // });
        }
      } catch (e) {
        console.error('Unable to continue form submission', e);
      }
    });
    formNode.parentNode.replaceChild(formReplacement, formNode);

    // Find label for email input label and add as a title attribute on the input
    try {
      const emailInput = formReplacement.querySelector('input[type="email"]');
      const inputLabel = formReplacement.querySelector(
        `label[for="${emailInput.id}"]`
      );
      emailInput.setAttribute('title', inputLabel.innerText);
    } catch (e) {
      console.error(
        'Slotted form does not have a correctly labeld email input'
      );
    }
  },
  methods: {
    closeModals() {
      this.modalOpen = false;
      this.confirmTermsCheck = false;
      this.signupComplete = false;
    },
    onTermsCheck() {
      // Clear terms error if the user checks the box
      if (this.confirmTermsCheck && this.termsFormError === this.termsError) {
        this.termsFormError = null;
      }
    },
    parseErrorMsg(errorWrapper) {
      const errorMsgs = errorWrapper.querySelectorAll('li');
      let combinedError = '';
      errorMsgs.forEach((msg) => {
        combinedError = `${combinedError}\n ${msg.textContent}`;
      });
      return combinedError;
    },
    async submitForm() {
      if (!this.confirmTermsCheck) {
        this.termsFormError = this.termsError;
        return;
      }
      this.submitDisabled = true;
      // Recaptcha Flow via Mixin
      this.peformRecaptcha()
        .then(async (canSubmit) => {
          if (canSubmit) {
            const FORM_URL =
              process.env.NODE_ENV === 'production'
                ? this.formAction
                : `${this.testHost}${this.formAction}`;
            await fetch(FORM_URL, {
              method: 'POST',
              mode: process.env.NODE_ENV === 'production' ? 'cors' : 'no-cors',
              body: this.formData,
            })
              .then((response) => {
			     if (response.status == 200) {
				      const dataLayer = window.dataLayer || [];
					  dataLayer.forEach((item, index) => {
						if (item.brand !== undefined) {
							dataLayer[index].uiEmailId = response.headers.get('encryptedEmailId'); 
						}
					  });
				  } 
                if (response.status >= 400 && response.status < 600) {
                  this.submitDisabled = false;
                  throw new Error(this.defaultErrorMessage);
                }
                return response;
              })
              .then((response) => {
                const htmlResponse = this.parser.parseFromString(
                  response,
                  'text/xml'
                );
                const errorWrapper = htmlResponse.querySelector(
                  '.validation-summary-errors'
                );
                if (errorWrapper) {
                  this.submitDisabled = false;
                  this.termsFormError = this.parseErrorMsg(errorWrapper);
                } else {
                  this.signupComplete = true;
                  pushEvent({
                    event: 'uaevent',
                    event_name: 'newsletter_registration',
                    ecommerce: 'undefined',
                    eventCategory: 'registration',
                    eventAction: 'newsletter',
                    eventLabel: this.taggingOrigin,
                    referral: this.taggingOrigin,
                  });
                }
              })
              .catch((error) => {
                this.submitDisabled = false;
                this.termsFormError = error.message;
              });
          }
        })
        .catch(() => {
          this.submitDisabled = false;
          this.termsFormError = this.defaultErrorMessage;
        });
    },
  },
};
</script>

<style lang="scss" src="./newsletter-signup.scss"></style>
